
import { useEffect, useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

// Material Dashboard 2 React components
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CircularProgress from "@mui/material/CircularProgress";

// Authentication layout components
import BasicLayoutLanding from "layouts/authentication/components/BasicLayoutLanding";

// Images
import bgImage from "assets/images/bg-sign-in-basic.jpg";

import AuthService from "services/auth-service";
import { AuthContext } from "context";

const MESSAGES = [
    "Connecting to demo environment...",
    "Setting up temporary profiles...",
    "Loading sample data...",
    "Preparing your dashboard...",
    "Almost ready..."
];

function LiveDemo() {
    const authContext = useContext(AuthContext);
    const [error, setError] = useState(null);
    const [messageIndex, setMessageIndex] = useState(0);

    const navigate = useNavigate();

    // Message rotation effect
    useEffect(() => {
        if (error) return;

        const interval = setInterval(() => {
            setMessageIndex(prev => (prev < MESSAGES.length - 1 ? prev + 1 : prev));
        }, 800); // Change message every 800ms

        return () => clearInterval(interval);
    }, [error]);

    useEffect(() => {
        // Prevent re-login if already authenticated and just redirect them securely
        if (authContext.isAuthenticated) {
            navigate("/dashboard");
            return;
        }

        const performDemoLogin = async () => {
            try {
                // [NEW] Clear any previous demo state to ensure tour resets
                localStorage.removeItem("token");
                sessionStorage.removeItem("demo_visited_stash");
                sessionStorage.removeItem("demo_visited_studio");
                sessionStorage.removeItem("demo_visited_hub");
                sessionStorage.removeItem("demo_visited_scout");
                sessionStorage.removeItem("demo_completion_shown");
                sessionStorage.removeItem("platform_scout_completed");
                sessionStorage.removeItem("insight_hub_welcome_shown");
                sessionStorage.removeItem("platform_scout_intro_shown");
                sessionStorage.removeItem("demo_stash_attempts");

                // Minimum 5s delay to show messages
                const minDelayPromise = new Promise(r => setTimeout(r, 5000));
                const loginPromise = AuthService.demoLogin();

                // Wait for both login and delay
                const [_, response] = await Promise.all([minDelayPromise, loginPromise]);

                if (response && response.access_token) {
                    // Pass null for workspaceId, and "/dashboard" for redirectPath
                    authContext.login(response.access_token, null, "/dashboard");
                } else {
                    throw new Error("Invalid response from server");
                }

            } catch (err) {
                console.error("Demo Login Failed Debug:", {
                    message: err.message,
                    url: err.config?.url,
                    baseURL: err.config?.baseURL,
                    status: err.status || err.response?.status,
                    data: err.response?.data
                });

                // [FIX] Extract detail from the standard error structure or use message
                const detail = err.errors?.[0]?.detail ||
                    err.response?.data?.errors?.[0]?.detail ||
                    err.message;

                setError(detail || "Failed to connect to Live Demo environment.");
            }
        };

        performDemoLogin();
    }, [authContext.isAuthenticated, authContext.login]);

    return (
        <BasicLayoutLanding image={bgImage}>
            <Card>
                <MDBox
                    variant="gradient"
                    bgColor="info"
                    borderRadius="lg"
                    coloredShadow="info"
                    mx={2}
                    mt={-3}
                    p={3}
                    mb={1}
                    textAlign="center"
                >
                    <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
                        Live Demo
                    </MDTypography>
                    <MDTypography variant="button" color="white" opacity={0.8}>
                        {error ? "Setup Failed" : "Setting up your environment..."}
                    </MDTypography>
                </MDBox>

                <MDBox pt={4} pb={3} px={3} textAlign="center">

                    {!error ? (
                        <MDBox display="flex" flexDirection="column" alignItems="center" py={3}>
                            <CircularProgress color="accent" />
                            <MDTypography variant="body2" color="text" mt={2} fontWeight="medium">
                                {MESSAGES[messageIndex]}
                            </MDTypography>
                        </MDBox>
                    ) : (
                        <MDBox>
                            <MDTypography variant="body2" color="error" mb={2}>
                                {error}
                            </MDTypography>

                            <MDTypography variant="caption" display="block" color="text" mb={2}>
                                The demo account might not be configured yet. <br />
                                Please try again later or register a free account.
                            </MDTypography>

                            <MDButton component={Link} to="/auth/login" variant="gradient" color="accent" fullWidth>
                                Back to Login
                            </MDButton>
                        </MDBox>
                    )}

                </MDBox>
            </Card>
        </BasicLayoutLanding>
    );
}

export default LiveDemo;
